import { Component, ViewEncapsulation, HostBinding, ChangeDetectionStrategy,
	ChangeDetectorRef, OnDestroy } from '@angular/core';
import { ICellRendererAngularComp } from '@ag-grid-community/angular';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ICellRendererParams } from '@ag-grid-community/core';
import { NgIf } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Subscription } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';

import { ProductA2c, PRODUCT_TYPE_F, PRODUCT_TYPE_FM, BocOpt } from '../../../../models/offer';
import { FormOffer } from '../../../../models/form-offer';
import { ProductService } from '../../../../services/product.api.service';
import { AppWizardService } from '../../../../services/app-wizard.service';
import { InputNumberDirective } from '../../../../../shared/directives/input-number.directive';

interface AddToCartProvider {
	onClickAddToCart?: (params?: ProductA2c, itemCount?: number) => void;
	inputParams(params: ICellRendererParams<unknown, unknown>): AddToCartParam | undefined;
}

interface AddToCartParam {
	buttonLabel: string;
	templateNumber: number;
	product: ProductA2c;
}

@Component({
	selector: 'copo-add-to-cart',
	templateUrl: './add-to-cart.component.html',
	styleUrl: './add-to-cart.component.css',
	changeDetection: ChangeDetectionStrategy.OnPush,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		NgIf,
		MatIconModule,
		FormsModule,
		ReactiveFormsModule,
		InputNumberDirective,
		MatTooltipModule,
		MatInputModule,
		TranslateModule
	]
})
export class AddToCartComponent implements ICellRendererAngularComp, OnDestroy {
	@HostBinding('class.passthrough') readonly passthrough = true;
	addToCartHandler!: (params?: ProductA2c, itemCount?: number) => void;

	params?: ICellRendererParams<unknown, unknown>;
	routerLinkParams: AddToCartParam | undefined;
	buttonLabel = '';
	disableAddToCart = true;
	numberControl = new FormControl('1');
	shipToEndUserChangeSub?: Subscription;
	product?: ProductA2c;
	readonly form: FormOffer;

	constructor(
		readonly changeDetectorRef: ChangeDetectorRef,
		private readonly productService: ProductService,
		private readonly appWizardService: AppWizardService
	) {
		this.form = this.appWizardService.getStateOfferForm();
	}

	agInit(params: ICellRendererParams<unknown, unknown>) {
		if (!params.colDef) {
			return;
		}
		this.params = params;
		this.routerLinkParams = (<AddToCartProvider> params.colDef.cellRendererParams).inputParams(params);
		this.buttonLabel = this.routerLinkParams ? this.routerLinkParams.buttonLabel : '';
		if (this.routerLinkParams) {
			this.product = { ...this.routerLinkParams.product };
			const shipToValue = <string | undefined> this.form.getFormShipTo().value;
			const endUserValue = <string | undefined> this.form.getFormEndUser().value;
			this.disableAddToCart = this.disableAddToCartForProduct(this.product, shipToValue, endUserValue);
		}
		if (this.disableAddToCart) {
			this.numberControl.disable();
		}
		this.addToCartHandler = (<AddToCartProvider> params.colDef.cellRendererParams)
			.onClickAddToCart ?? (() => { /* no-op */ });

		this.shipToEndUserChangeSub = this.productService.shipToEndUserChangeEvent.subscribe((boc: BocOpt) => {
			if (this.product) {
				this.disableAddToCart = this.disableAddToCartForProduct(this.product, boc.shipTo, boc.endUser);
				if (this.disableAddToCart) {
					this.numberControl.disable();
				} else {
					this.numberControl.enable();
				}
			}
		});
	}

	refresh(params: ICellRendererParams<unknown, unknown>) {
		this.params = params;
		return true;
	}

	addToCart() {
		if (this.product && this.numberControl.value) {
			this.addToCartHandler(this.product, Number(this.numberControl.value));
		}
	}

	ngOnDestroy(): void {
		if (this.shipToEndUserChangeSub) {
			this.shipToEndUserChangeSub.unsubscribe();
		}
	}

	private disableAddToCartForProduct(product?: ProductA2c, shipTo?: string, endUser?: string): boolean {
		if (product) {
			const productShipTo = product.shipToPartyCoded?.replace(/^0+/, '');
			const productEndUser = product.endUserIdentificationCoded?.replace(/^0+/, '');
			if (product.productType === PRODUCT_TYPE_F) {
				if ((shipTo && productShipTo !== shipTo) || (endUser && productEndUser !== endUser)) {
					return true;
				}
			} else if (product.productType === PRODUCT_TYPE_FM) {
				const templateAndDetail = this.appWizardService.getState().ui.getTemplateNumberDetails(
					Number(product.customerTemplateNumber)
				);
				if (templateAndDetail) {
					const variableNames = Object.getOwnPropertyNames(templateAndDetail.properties);
					if ((!variableNames.includes('shipToParty') && shipTo && productShipTo !== shipTo)
						|| ((!variableNames.includes('endUser') && endUser && productEndUser !== endUser))) {
						return true;
					}
				}
			}
		}
		return false;
	}
}
